import ExcelJS from 'exceljs';
import {
  QUY_DOI_DON_VI_TINH_EXPORT_TITLE,
  QUY_DOI_DON_VI_TINH_FILE_NAME,
  QUY_DOI_DON_VI_TINH_SHEET_NAME
} from './constant';
import { fetchAllQuyDoiDonViTinhChiTiet } from '@/hooks/queries/useQuyDoiDonViTinhChiTiet';
import { QUY_DOI_DON_VI_TINH_COLUMNS } from './column-mapping';
import { QuyDoiDonViTinhChiTietExcelOptions } from './types';
import { QuyDoiDonViTinhChiTiet } from '@/types/schemas';

const buildFileName = (base: string) => {
  const timestamp = new Date().toISOString().slice(0, 10);
  return `${base}-${timestamp}.xlsx`;
};

const applyHeaderStyles = (row: ExcelJS.Row) => {
  row.eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      left: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      right: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } }
    };
  });
};

const createWorksheet = (sheetName: string) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);
  return { workbook, worksheet };
};

const addHeaderInfo = (worksheet: ExcelJS.Worksheet, columnsLength: number, headerInfo: string[] = []) => {
  headerInfo.forEach((line, index) => {
    const row = worksheet.addRow([line]);
    worksheet.mergeCells(row.number, 1, row.number, columnsLength);
    const cell = row.getCell(1);
    cell.font = { bold: index === 0, size: 12 };
    cell.alignment = { horizontal: 'left', vertical: 'middle' };
  });

  if (headerInfo.length) {
    worksheet.addRow([]);
  }
};

export const exportQuyDoiDonViTinhChiTietExcel = async (
  rows: QuyDoiDonViTinhChiTiet[] = [],
  options?: QuyDoiDonViTinhChiTietExcelOptions
) => {
  const columns = options?.columns || QUY_DOI_DON_VI_TINH_COLUMNS;
  const title = options?.title || QUY_DOI_DON_VI_TINH_EXPORT_TITLE;
  const sheetName = options?.sheetName || QUY_DOI_DON_VI_TINH_SHEET_NAME;
  const fileName = buildFileName(options?.fileName || QUY_DOI_DON_VI_TINH_FILE_NAME);
  const headerInfo = options?.headerInfo || [];

  const { workbook, worksheet } = createWorksheet(sheetName);

  addHeaderInfo(worksheet, columns.length, headerInfo);

  const titleRow = worksheet.addRow([title]);
  worksheet.mergeCells(titleRow.number, 1, titleRow.number, columns.length);
  const titleCell = titleRow.getCell(1);
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

  worksheet.addRow([]);

  const headerRow = worksheet.addRow(columns.map(col => col.header));
  applyHeaderStyles(headerRow);

  rows.forEach(row => {
    const values = columns.map(col => col.resolver(row) ?? '');
    const excelRow = worksheet.addRow(values);

    columns.forEach((col, columnIndex) => {
      const cell = excelRow.getCell(columnIndex + 1);
      if (col.numFmt && typeof values[columnIndex] === 'number') {
        cell.numFmt = col.numFmt;
      }
    });
  });

  columns.forEach((col, index) => {
    const column = worksheet.getColumn(index + 1);
    column.width = col.width || 18;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const exportQuyDoiDonViTinhChiTietFromApi = async ({
  entitySlug,
  queryParams = {},
  pageSize = 200,
  excelOptions
}: {
  entitySlug: string;
  queryParams?: Record<string, unknown>;
  pageSize?: number;
  excelOptions?: QuyDoiDonViTinhChiTietExcelOptions;
}) => {
  const rows = await fetchAllQuyDoiDonViTinhChiTiet(entitySlug, queryParams, pageSize);
  return exportQuyDoiDonViTinhChiTietExcel(rows, excelOptions);
};
